import {
  Component,
  ChangeDetectorRef,
  DestroyRef,
  effect,
  inject,
  input,
  linkedSignal,
  output
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Location } from '@angular/common';
import { TranslateDirective } from '@ngx-translate/core';
import { finalize } from 'rxjs/operators';
import {
  BlockDirective,
  ButtonComponent,
  DataAnalyticsTaggingConstants,
  DataAnalyticsTaggingDirective,
  DataAnalyticsTaggingEvent,
  IconComponent,
  NumberPipe,
  PagePanelComponent,
  TranslatePipe,
  ToastMessageService
} from '@tdwb/core';
// eslint-disable-next-line td-wb-rules/cross-lazyload-module-import-rule
import { LearnLessonInfoComponent, LearnLoadingComponent, LearnPlayerComponent } from '@tdwb/lazy/learn';
import { LearnRestService } from '@tdwb/webbroker/core';
import { Lesson } from '../domain/learn.domain';
@Component({
  selector: 'td-wb-learn-lesson-content',
  templateUrl: 'learn-lesson-content.component.html',
  styleUrls: ['learn-lesson-content.component.scss'],
  imports: [
    PagePanelComponent,
    TranslateDirective,
    DataAnalyticsTaggingDirective,
    LearnLoadingComponent,
    LearnLessonInfoComponent,
    ButtonComponent,
    BlockDirective,
    IconComponent,
    NumberPipe,
    TranslatePipe,
    LearnPlayerComponent
  ]
})
export class LearnLessonContentComponent {
  private readonly learnRestService = inject(LearnRestService);
  private readonly toastMessageService = inject(ToastMessageService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly location = inject(Location);
  private readonly destroyRef = inject(DestroyRef);
  readonly id = input<string>();
  readonly loadingIndicatorInput = input(true, { alias: 'loadingIndicator' });
  readonly section = input<string>();
  readonly lessonLoaded = output<boolean>();
  lesson: Lesson;
  likeDisabled: boolean = false;
  saveDisabled: boolean = false;
  eventData: DataAnalyticsTaggingEvent = {};
  protected readonly loadingIndicator = linkedSignal({
    source: this.loadingIndicatorInput,
    computation: (loadingIndicator: boolean): boolean => loadingIndicator
  });

  constructor() {
    effect((): void => {
      const id = this.id();
      if (id !== undefined) {
        this.retrieveLessonDetails(id);
      }
    });
  }

  retrieveLessonDetails(id: string): void {
    this.learnRestService.retrieveLesson(id)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.changeDetectorRef.markForCheck())
      )
      .subscribe({
        next: (lesson: Lesson): void => {
          this.lesson = lesson;
          this.loadingIndicator.set(false);
          this.lessonLoaded.emit(true);
        },
        error: (): void => {
          this.loadingIndicator.set(false);
          this.lessonLoaded.emit(true);
        }
      });
  }

  getLike(): boolean {
    return this.lesson.like === 5;
  }

  getLikeCount(): number {
    return this.lesson.likeCount === 0 ? undefined : this.lesson.likeCount;
  }

  toggleLike(): void {
    const resetLike = this.lesson.like;
    const like = this.lesson.like === 5 ? 0 : 5;
    this.lesson.like = like;
    //TODO: create enum?
    const action = like === 0 ? 'unliked' : 'liked';
    this.eventData = DataAnalyticsTaggingConstants.getLinkClickEventData(`tdw:wbt:learn:lesson:${this.section()}:${action}:${this.lesson.title}`);
    this.likeDisabled = true;
    this.learnRestService
      .postLike({
        id: this.lesson.contentMetaData.id,
        like
      })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.changeDetectorRef.markForCheck())
      )
      .subscribe({
        next: (count: number): void => {
          this.lesson.likeCount = count;
          this.likeDisabled = false;
        },
        error: (): void => {
          this.lesson.like = resetLike;
          this.likeDisabled = false;
        }
      });
  }

  toggleSave(): void {
    this.saveDisabled = true;
    this.learnRestService.toggleVideoBookMark(this.lesson)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.changeDetectorRef.markForCheck())
      )
      .subscribe({
        next: (): void => {
          this.lesson.saved = !this.lesson.saved;
          //TODO: create enum?
          const action = this.lesson.saved ? 'saved' : 'unsaved';
          this.eventData = DataAnalyticsTaggingConstants.getLinkClickEventData(`tdw:wbt:learn:${this.section()}:${action}:${this.lesson.title}`);
          this.saveDisabled = false;
          const messageKey = this.lesson.saved ? 'tdWb.learn.notification.SAVED' : 'tdWb.learn.notification.UNSAVED';
          this.toastMessageService.setToast(messageKey);
        },
        error: (): void => {
          this.saveDisabled = false;
        }
      });
  }

  back(): void {
    this.location.back();
  }
}
